'use client'

import { useState, useRef, useEffect } from 'react'
import { LoadScript } from '@react-google-maps/api'
import GoogleMap from '../GoogleMap'
import DealerCards from './DealerCards'
import { useFetchLocations } from '@/hooks/useFetchLocations'
import TabbedSearch from '../TabbedSearch'
import SearchParts from '../SearchParts'
import Icon from '../svg/icon'
import clsx from 'clsx'
import type { SearchHeroFragment } from '@/lib/types/datocms/graphql/generated'

type Props = Pick<SearchHeroFragment, 'searchTitle' | 'searchDescription' | 'searchCta'>

export function MapSection({ searchTitle, searchDescription, searchCta }: Props) {
	const observerRef = useRef<HTMLDivElement | null>(null)
	const [viewMode, setViewMode] = useState<'map' | 'list'>('map')
	const [typeFilter, setTypeFilter] = useState<string>('Sales')
	const [zipCode, setZipCode] = useState('')
	const [mapDataLoaded, setMapDataLoaded] = useState(false)

	// Fetch data
	const { locations, isLoading, isError } = useFetchLocations(typeFilter, zipCode, mapDataLoaded)

	// Intersection Observer for map data
	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && !mapDataLoaded) {
					setMapDataLoaded(true)
				}
			},
			{ threshold: 0.1, rootMargin: '200px 0px' }
		)

		if (observerRef.current) observer.observe(observerRef.current)

		return () => {
			if (observerRef.current) observer.unobserve(observerRef.current)
		}
	}, [])

	return (
		<section ref={observerRef} className="map-section bg-white-frosted @container">
			<LoadScript googleMapsApiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!} language="en">
				{/* TabbedSearch Component */}

				<TabbedSearch
					currentFilter={typeFilter}
					onFilterChange={setTypeFilter}
					zipCode={zipCode}
					onZipCodeChange={setZipCode}
				/>

				{/* Results */}
				<div
					className={clsx(
						'@3xl:-mt-24 @3xl:pt-32 @3xl:pb-12 @3xl:common-x-padding flex-1 flex max-w-screen-2xl mx-auto min-h-[900px]',
						typeFilter === 'Parts' && 'hidden'
					)}
				>
					{!isError && isLoading && <p className="mt-10 heading-size-5">Loading locations...</p>}
					{!isLoading && isError && <p className="mt-10 heading-size-5">Error loading locations</p>}
					{!isLoading && !isError && locations.length > 0 && (
						<div className="w-full">
							<div className="flex justify-between items-center common-x-padding @3xl:px-0 py-8">
								<span className="heading-size-5">
									Showing <span className="text-blue">{locations.length}</span> results
								</span>
								<div className="flex gap-3 my-4 justify-center">
									<button
										onClick={() => setViewMode('map')}
										aria-label="Map View"
										className={`p-2rounded-md ${viewMode === 'map' ? ' text-blue' : ' text-gray'}`}
									>
										<Icon
											icon="LocationPinSimple"
											className={clsx(
												'w-4',
												viewMode === 'map' ? 'text-blue' : 'text-blue-dark/20'
											)}
										/>
									</button>
									<button
										onClick={() => setViewMode('list')}
										aria-label="List View"
										className={`px-2 rounded-md  ${
											viewMode === 'list' ? ' text-blue' : 'text-blue-dark/20'
										}`}
									>
										<Icon
											icon="List"
											className={clsx(
												'w-4',
												viewMode === 'list' ? 'text-blue' : 'text-blue-dark/20'
											)}
										/>
									</button>
								</div>
							</div>
							{viewMode === 'map' ? (
								<GoogleMap locations={locations} />
							) : (
								<DealerCards locations={locations} />
							)}
						</div>
					)}
					{!isLoading && !isError && locations.length === 0 && <p>No results found</p>}
				</div>

				{typeFilter === 'Parts' && (
					<SearchParts
						searchTitle={searchTitle}
						searchDescription={searchDescription}
						searchCta={searchCta}
					/>
				)}
			</LoadScript>
		</section>
	)
}
